/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useEffect, useRef } from "react";
import WaveSurfer from "wavesurfer.js";
import WaveSurferTimeline from "wavesurfer.js/dist/plugin/wavesurfer.timeline.min.js";
import { WaveForm } from "wavesurfer-react";
import TimelinePlugin from "wavesurfer.js/dist/plugin/wavesurfer.timeline.min.js";
import { useTheme } from "../../../ThemeContext";
import Tooltip from "@mui/material/Tooltip";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import "./CallHistoryAudioPlayer.css";

/**
 * CallHistoryAudioPlayer - Audio player for Call History V2
 * Takes all data as props (no context dependency)
 * Works with new getCallDetails API data structure
 * NOTE: Transcript is now rendered separately in CallTranscriptView
 */
export default function CallHistoryAudioPlayer({
  audioUrl,
}) {
  const { currentTheme, themeMode } = useTheme();
  const wavesurferRef = useRef(null);
  const timelineRef = useRef(null);
  const [audioSrc, setAudioSrc] = useState(null);

  const volumeRef = useRef(null);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark-mode", themeMode === "dark");
  }, [themeMode]);

  useEffect(() => {
    if (!audioUrl) return;

    // Check if this is a problematic bucket
    const isProblematicBucket = audioUrl.includes("storage.googleapis.com/teasaef");

    if (isProblematicBucket) {
      console.warn("⚠️ This audio is from a bucket without CORS configuration. Waveform visualization may not work.");
      console.warn('🔧 Backend team needs to configure CORS on the "teasaef" bucket');
    }

    const wavesurfer = WaveSurfer.create({
      container: wavesurferRef.current,
      waveColor: currentTheme.callhistory.waveColor,
      progressColor: currentTheme.callhistory.progressColor,
      cursorWidth: 1,
      scrollParent: true,
      hideScrollbar: true,
      cursorColor: "#6b42de",
      barWidth: 2,
      barGap: 6,
      height: 50,
      normalize: true,
      responsive: true,
      backend: "WebAudio",
      mediaControls: true,
      mono: true,
      plugins: [
        WaveSurferTimeline.create({
          container: timelineRef.current,
          scrollParent: true,
          primaryFontColor: themeMode === 'dark' ? '#fff' : '#000',
          secondaryFontColor: themeMode === 'dark' ? '#fff' : '#000',
          offset:0
        }),
      ],
    });
    
    wavesurfer.load(audioUrl);
    setAudioSrc(wavesurfer);

    return () => {
      wavesurfer.destroy();
    };
  }, [audioUrl, currentTheme, themeMode]);

  useEffect(() => {
    if (!audioSrc) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audioSrc.on("play", onPlay);
    audioSrc.on("pause", onPause);
    return () => {
      audioSrc.un("play", onPlay);
      audioSrc.un("pause", onPause);
    };
  }, [audioSrc]);

  function handleVolumeChange(e) {
    const newVolume = parseFloat(e.target.value);
    audioSrc.setVolume(newVolume);
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) setIsMuted(false);
    if (newVolume === 0 && !isMuted) setIsMuted(true);
  }

  const handlePlayPause = () => {
    audioSrc.playPause();
  };

  const handleMuteUnmute = () => {
    if (audioSrc) {
      if (isMuted) {
        audioSrc.setVolume(volume);
      } else {
        audioSrc.setVolume(0);
      }
      setIsMuted(!isMuted);
    }
  };

  return (
    <div style={{ 
      height: "100%", 
      display: "flex", 
      flexDirection: "column",
      overflow: "hidden"
    }}>
      <div
        style={{
          background: currentTheme.callhistory.waveBackground,
          borderRadius: "1vh",
          flexShrink: 0,
        }}
        ref={wavesurferRef}
      />
      <div ref={timelineRef} style={{ flexShrink: 0 }} />
      <div style={{ flexShrink: 0 }}>
        <WaveForm
          id="waveform"
          waveSurfer={wavesurferRef.current}
          onWaveformReady={(waveform) => {
            waveform.addPlugin(
              TimelinePlugin.create({
                container: waveform.container,
                scrollParent: true,
              })
            );
          }}
        />
      </div>

      {/* Controls Row */}
      <div style={{
        display: "flex",
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "space-between",
        margin: "2 0",
        width: "100%"
      }}>
        {/* Play/Pause on the left */}
        <Tooltip title={isPlaying ? "Pause" : "Play"}>
          <span onClick={handlePlayPause} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
            {isPlaying ? (
              <PauseIcon style={{ fontSize: "2rem", color: themeMode === "dark" ? "#fff" : "#333" }} />
            ) : (
              <PlayArrowIcon style={{ fontSize: "2rem", color: themeMode === "dark" ? "#fff" : "#333" }} />
            )}
          </span>
        </Tooltip>
        {/* Mute/Unmute and Volume on the right */}
        <div style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
          <Tooltip title={isMuted ? "Unmute" : "Mute"}>
            <span onClick={handleMuteUnmute} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
              {isMuted ? (
                <VolumeOffIcon style={{ fontSize: "1.75rem", color: themeMode === "dark" ? "#fff" : "#333" }} />
              ) : (
                <VolumeUpIcon style={{ fontSize: "1.75rem", color: themeMode === "dark" ? "#fff" : "#333" }} />
              )}
            </span>
          </Tooltip>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            ref={volumeRef}
            onChange={handleVolumeChange}
            style={{
              marginLeft: 0,
              verticalAlign: "middle",
              height: "0.375rem",
              width: "7.5rem"
            }}
          />
        </div>
      </div>
    </div>
  );
}

